import './UserEditPage.css'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  userDeleteSpec,
  userGetSpec,
  userSaveSpec,
  UserRole,
  TenantUserRole,
  type TenantUserRow,
  type UserRow,
} from 'aevocam-rest-api-spec'
import { useApi } from '@/hooks/useApi'
import { routes } from '@/utilities/routes'
import { BasicPage } from '@/components/layout/BasicPage'
import { Button } from '@/components/action/Button'
import { Notice } from '@/components/action/Notice'
import { TextBox } from '@/components/scalar/TextBox'
import { SelectBox } from '@/components/scalar/SelectBox'
import { FilterTextBox } from '@/components/scalar/FilterTextBox'
import { toastError, toastSuccess, toastWarning } from '@/components/action/Toaster'
import { toFullDate } from '@/utilities/formatters'
import { DataGrid, type DataGridColumn } from '@/components/dataGrid/DataGrid'
import type { DataGridState } from '@/components/dataGrid/createDataGridState'

const USER_ROLE_OPTIONS: { label: string; value: UserRole }[] = [
  { label: 'User', value: UserRole.User },
  { label: 'Admin', value: UserRole.Admin },
]

const tenantColumns: DataGridColumn<TenantUserRow>[] = [
  {
    id: 'tenantName',
    label: 'Tenant',
    link: (_value, { row }) =>
      row.tenantId != null ? routes.home(String(row.tenantId)) : undefined,
    className: () => 'Ellipsis',
    style: () => ({ maxWidth: '24vw' }),
    render: (value, { row }) =>
      value?.trim()
      || (row.tenantId != null ? `Tenant ${row.tenantId}` : ''),
  },
  {
    id: 'tenantId',
    label: 'ID',
    align: 'right',
    className: () => 'NoWrap',
    render: value => (value != null ? String(value) : ''),
  },
  {
    id: 'role',
    label: 'Role',
    align: 'center',
    render: (value: TenantUserRole | undefined) =>
      value != null ? TenantUserRole[value] : '',
  },
]

export function UserEditPage() {
  const { id: userId } = useParams<{ id: string }>()
  const api = useApi({ requireLogin: true, adminOnly: true })
  const navigate = useNavigate()

  const [user, setUser] = useState<UserRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<UserRole>(UserRole.User)
  const [disabled, setDisabled] = useState(false)
  const [tenantFilterText, setTenantFilterText] = useState('')

  //--------------------------------------------------------------------------------
  const loadUser = useCallback((value: UserRow) => {
    setUser(value)
    setName(value.name ?? '')
    setEmail(value.email ?? '')
    setRole(value.role ?? UserRole.User)
    setDisabled(value.disabled ?? false)
    setTenantFilterText('')
  }, [])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    async function fn() {
      if (!userId) return
      setLoading(true)
      try {
        const res = await api.request(userGetSpec, {
          path: { user: userId },
        })
        if (res.status === 200 && res.data?.user) {
          loadUser(res.data.user)
        } else {
          toastError(res.error ?? 'Failed to load user')
        }
      } catch {
        toastError('Failed to load user')
      } finally {
        setLoading(false)
      }
    }
    void fn()
  }, [userId, loadUser])

  //--------------------------------------------------------------------------------
  async function handleSave(action?: 'enable' | 'disable') {
    if (!userId || !user) return
    const nextDisabled =
      action === 'enable' ? false : action === 'disable' ? true : disabled
    const res = await api.request(userSaveSpec, {
      path: { user: userId },
      body: {
        user: {
          ...user,
          name: name || undefined,
          email: email || undefined,
          role,
          disabled: nextDisabled,
        },
      },
    })
    if (res.status === 200 && res.data?.user) {
      const saved = res.data.user
      setUser(saved)
      loadUser(saved)
      toastSuccess('Saved')
    } else {
      toastError(res.error ?? 'Failed to save user')
    }
  }

  //--------------------------------------------------------------------------------
  const handleDelete = useCallback(async () => {
    if (!userId || !user) return
    if (!confirm('Delete this user? This cannot be undone.')) return
    const res = await api.request(userDeleteSpec, {
      path: { user: userId },
    })
    if (res.status === 200) {
      toastWarning('User deleted')
      navigate(routes.adminUsers())
    } else {
      toastError(res.error ?? 'Failed to delete user')
    }
  }, [userId, user, navigate])

  //--------------------------------------------------------------------------------
  const tenantGridState = useMemo((): DataGridState<TenantUserRow> => {
    const tenants = user?.tenants ?? []
    const filter = tenantFilterText.trim().toLowerCase()
    const rows = filter
      ? tenants.filter(t => {
        const name = t.tenantName?.trim().toLowerCase() ?? ''
        const id = t.tenantId != null ? String(t.tenantId) : ''
        return name.includes(filter) || id.includes(filter)
      })
      : tenants
    return {
      rows,
      totalRows: rows.length,
      skip: 0,
      take: Math.max(rows.length, 1),
    }
  }, [user?.tenants, tenantFilterText])

  //--------------------------------------------------------------------------------
  if (loading) return <main><p>Loading…</p></main>
  if (!user) return <main><p>User not found</p></main>

  //--------------------------------------------------------------------------------
  return (
    <BasicPage
      title={'User: ' + (name.trim() || email.trim())}
      className='UserEditPage'
      toolbarButtons={<>
        <Button
          label='Users'
          icon='chevronLeft'
          href={routes.adminUsers()}
        />
        <Button
          variant='primary'
          label='Save'
          icon='save'
          onClick={() => handleSave()}
        />
        <Button
          label={disabled ? 'Disabled' : 'Enabled'}
          icon={disabled ? 'regSquare' : 'checkSquare'}
          title={disabled ? 'Enable user' : 'Disable user'}
          onClick={() => handleSave(disabled ? 'enable' : 'disable')}
        />
        <Button
          label='Delete'
          icon='trash'
          onClick={handleDelete}
        />
      </>}
    >

      {disabled && (
        <Notice variant='warning' icon='exclamationTriangle'>
          This user account is disabled.
        </Notice>
      )}

      {user.deletedAt != null && (
        <Notice variant='warning' icon='exclamationTriangle'>
          This user is marked deleted.
        </Notice>
      )}

      <div className='FlexBricks'>

        <div className='Cutout Wrapped' style={{ maxWidth: '35rem'}}>
          <h2>Account</h2>
          <TextBox
            label='User ID'
            readOnly
            width={120}
            value={user.id != null ? String(user.id) : ''}
          />
          <TextBox
            label='Name'
            width={320}
            value={name}
            setValue={setName}
          />
          <TextBox
            label='Email'
            width={360}
            value={email}
            setValue={setEmail}
          />
          <SelectBox<UserRole>
            label='Role'
            options={USER_ROLE_OPTIONS}
            valueToString={v => (v != null ? String(v) : '')}
            valueFromString={s => Number(s) as UserRole}
            value={role}
            setValue={setRole}
          />
          <TextBox
            label='Created'
            readOnly
            width={150}
            value={user.createdAt != null ? toFullDate(user.createdAt) : ''}
          />
        </div>

        <div className='Cutout Tenants'>
          <h2>Tenants</h2>
          <FilterTextBox
            label='Filter'
            value={tenantFilterText}
            setValue={setTenantFilterText}
          />

          <DataGrid
            state={tenantGridState}
            setState={() => {}}
            idColumn='tenantId'
            columns={tenantColumns}
          />
        </div>
      </div>

    </BasicPage>
  )
}
